import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleHelpIcon,
  RefreshCwIcon,
  SearchXIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react";

import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { errorMessage, isRateLimited, isUpstreamError } from "~/lib/api";
import { formatDateTime, formatIDR } from "~/lib/format";
import { useProviderStatus, type ProviderStatusResult } from "~/lib/queries";
import { cn } from "~/lib/utils";

/**
 * A result older than this is dimmed and labelled. It is not cleared: the
 * comparison is still the last thing we know, and hiding it would lose the
 * answer someone just fetched. It stops presenting itself as current.
 */
const STALE_AFTER_MS = 60_000;

/**
 * "Altura says X, DurianPay says Y" — and what that means.
 *
 * Never automatic. Every click is an outbound call to a partner, and firing one
 * on every page view turns idle browsing into load on DurianPay at exactly the
 * times it can least afford it.
 *
 * The comparison is only half the value. Two columns of statuses still leave
 * someone at two in the morning working out the implication, so the verdict is
 * stated in words, and where there is an obvious next place to look, it links
 * there.
 *
 * What it deliberately does not do: reconcile. There is no "sync from provider"
 * and no "mark as settled to match". If the two disagree, the fix goes through
 * the webhook or settlement path — an admin correction here would be a second,
 * unaudited way for a payment to change state.
 */
export function ProviderStatusPanel({ orderId }: { orderId: string }) {
  const check = useProviderStatus(orderId);
  const result = check.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Provider status</CardTitle>
        <CardDescription>
          Ask DurianPay what it thinks the state of this order is, right now.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <Button
          variant={result ? "outline" : "default"}
          size="sm"
          disabled={check.isPending}
          onClick={() => check.mutate()}
        >
          <RefreshCwIcon className={cn("size-3.5", check.isPending && "animate-spin")} />
          {check.isPending ? "Checking…" : result ? "Check again" : "Check provider"}
        </Button>

        {check.error ? <LookupFailed error={check.error} /> : null}

        {result ? <Result result={result} /> : null}

        {!result && !check.error && !check.isPending ? (
          <p className="text-xs text-muted-foreground">
            Nothing is fetched until you ask. Each check is a live request to DurianPay.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Result({ result }: { result: ProviderStatusResult }) {
  const stale = useIsStale(result.checked_at);

  if (!result.available) {
    return (
      <div className="space-y-3">
        <NotChecked result={result} />
        <Freshness checkedAt={result.checked_at} stale={stale} />
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", stale && "opacity-60")}>
      <Comparison result={result} />
      <VerdictLine result={result} />
      <Freshness checkedAt={result.checked_at} stale={stale} />
    </div>
  );
}

/**
 * The two views, side by side, each in its own vocabulary.
 *
 * DurianPay's status is never translated into our PaymentStatus enum. Doing so
 * would hide precisely the disagreements this panel exists to surface — and
 * "INITIATED vs completed" is a more useful thing to read than two identical
 * normalised words.
 */
function Comparison({ result }: { result: ProviderStatusResult }) {
  return (
    <div className="grid grid-cols-2 gap-3 rounded-lg border p-3">
      <div className="min-w-0 space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Altura</p>
        <StatusBadge status={result.altura.payment_status} />
        <p className="text-sm tabular-nums">{formatIDR(result.altura.amount)}</p>
      </div>

      <div className="min-w-0 space-y-1.5 border-l pl-3">
        <p className="text-xs font-medium text-muted-foreground">
          DurianPay
          {result.provider ? (
            <span className="ml-1 font-normal opacity-70">({result.provider.kind})</span>
          ) : null}
        </p>
        {result.provider ? (
          <>
            {/* Not a StatusBadge: that maps our own status vocabulary onto
                tones, and colouring a provider word by accidental overlap
                would be worse than not colouring it. */}
            <p className="font-mono text-[0.8125rem] break-all">{result.provider.status}</p>
            <p className="text-sm tabular-nums">
              {result.provider.amount ? (
                formatIDR(result.provider.amount)
              ) : (
                <span
                  className="text-xs text-muted-foreground"
                  title="The provider sent an amount we cannot read unambiguously — it is in the raw response below."
                >
                  no comparable amount
                </span>
              )}
            </p>
            {result.provider.updated_at ? (
              <p className="text-xs text-muted-foreground">
                {formatDateTime(result.provider.updated_at)}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">no record</p>
        )}
      </div>
    </div>
  );
}

interface VerdictCopy {
  icon: LucideIcon;
  tone: string;
  title: string;
  body: React.ReactNode;
}

/**
 * What the panel says when the two sides cannot be compared automatically.
 *
 * Also the fallback for any verdict the server grows that this build does not
 * know about: showing the raw response and admitting we cannot classify it is
 * always safe, where guessing at a new verdict's meaning is not.
 */
const UNCLASSIFIED: VerdictCopy = {
  icon: CircleHelpIcon,
  tone: "border-border bg-muted/40 text-muted-foreground",
  title: "The two cannot be compared automatically",
  body: "DurianPay answered with a status we do not classify. The raw response below is the whole answer.",
};

/** How each verdict reads, and where it points. */
const VERDICTS: Record<string, VerdictCopy> = {
  agree: {
    icon: CircleCheckIcon,
    tone: "border-success/30 bg-success/5 text-success",
    title: "Both agree",
    // Said plainly and then stopped, so nobody keeps digging through a page
    // that has already answered the question.
    body: "Altura and DurianPay tell the same story about this payment. Nothing to chase here.",
  },
  provider_ahead: {
    icon: TriangleAlertIcon,
    tone: "border-warning/30 bg-warning/5 text-warning",
    title: "DurianPay has this payment and Altura does not",
    body: "The webhook never arrived, or arrived and failed to process. That is the next place to look.",
  },
  altura_ahead: {
    icon: TriangleAlertIcon,
    tone: "border-warning/30 bg-warning/5 text-warning",
    title: "Altura believes this payment resolved and DurianPay does not",
    body: "Rarer than the reverse, and stranger. Check the webhook that moved this order, and what it actually said.",
  },
  amount_mismatch: {
    icon: CircleAlertIcon,
    tone: "border-destructive/30 bg-destructive/5 text-destructive",
    title: "Both say paid, for different amounts",
    // Flagged loudly on purpose. This is not sync lag that resolves itself.
    body: "This is a real discrepancy, not a delay. Do not close it by assuming one side is right — the settlement figures decide it.",
  },
  no_provider_record: {
    icon: SearchXIcon,
    tone: "border-border bg-muted/40 text-muted-foreground",
    title: "DurianPay has no record of this",
    body: "This order may never have reached the provider. Check whether payment creation actually succeeded.",
  },
  unknown: UNCLASSIFIED,
};

function VerdictLine({ result }: { result: ProviderStatusResult }) {
  const verdict = VERDICTS[result.verdict] ?? UNCLASSIFIED;
  const Icon = verdict.icon;

  return (
    <div className={cn("space-y-2 rounded-lg border p-3", verdict.tone)}>
      <p className="flex items-start gap-2 text-sm font-medium">
        <Icon className="mt-0.5 size-4 shrink-0" />
        {verdict.title}
      </p>
      <p className="text-xs text-muted-foreground">{verdict.body}</p>

      {/* One query away thanks to PRO-1023, and the first thing anyone wants
          after seeing this verdict. */}
      {result.verdict === "provider_ahead" || result.verdict === "altura_ahead" ? (
        <Button
          variant="ghost"
          size="xs"
          render={
            <Link
              to="/webhook-events"
              search={{ order_ref_id: result.order_id, status: "all" }}
            />
          }
        >
          This order's webhook events
          <ArrowRightIcon className="size-3" />
        </Button>
      ) : null}

      {result.provider?.raw ? <RawResponse raw={result.provider.raw} /> : null}
    </div>
  );
}

function RawResponse({ raw }: { raw: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        {open ? "Hide" : "Show"} DurianPay's raw response
      </button>
      {open ? (
        <pre className="mt-2 max-h-60 overflow-auto rounded-md border bg-background p-2 font-mono text-[0.6875rem] text-foreground">
          {JSON.stringify(raw, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

/**
 * The two reasons no lookup happened, each with its own sentence.
 *
 * "Failed to check provider" for an order that simply has no psp_payment_id is
 * the message that sends someone chasing an outage that is not happening.
 */
function NotChecked({ result }: { result: ProviderStatusResult }) {
  const held = Object.entries(result.identifiers);

  if (result.reason === "provider_not_configured") {
    return (
      <div className="rounded-lg border bg-muted/40 p-3 text-sm">
        <p className="font-medium">No provider is configured on this server</p>
        <p className="mt-1 text-xs text-muted-foreground">
          <code className="font-mono">PSP_BASE_URL</code> is unset in this environment, so
          there is nothing to ask. Nothing is wrong with this order.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-muted/40 p-3 text-sm">
      <p className="font-medium">This order has no provider identifiers</p>
      <p className="mt-1 text-xs text-muted-foreground">
        No <code className="font-mono">psp_payment_id</code> or{" "}
        <code className="font-mono">psp_order_id</code> was ever recorded, so there is nothing
        to look up. Common on older and v1-created orders, and it usually means the payment
        was never created at DurianPay at all. This is not an outage.
      </p>
      {held.length > 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          What we do hold:{" "}
          {held.map(([key, value], index) => (
            <span key={key}>
              {index > 0 ? ", " : ""}
              <code className="font-mono">
                {key}={value}
              </code>
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

/** The two failure shapes that are genuinely failures. */
function LookupFailed({ error }: { error: unknown }) {
  if (isRateLimited(error)) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
        <p className="font-medium text-warning">Too many checks</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Each check is a live request to DurianPay, so they are limited per person. Wait a
          moment and try again — this is our limit, not theirs, and nothing is broken.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
      <p className="font-medium text-destructive">
        {isUpstreamError(error) ? "DurianPay did not answer" : "The check could not be made"}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {errorMessage(error)}
        {isUpstreamError(error)
          ? " — this says nothing about the order itself; it is the provider or the network between us."
          : null}
      </p>
    </div>
  );
}

/**
 * When the answer was fetched, counting up.
 *
 * A comparison that silently persists across a page navigation is eventually
 * read as current when it is not, and "DurianPay says settled" being eleven
 * minutes old is exactly the kind of thing someone acts on without checking.
 */
function Freshness({ checkedAt, stale }: { checkedAt: string; stale: boolean }) {
  const age = useAge(checkedAt);

  return (
    <p className={cn("text-xs", stale ? "text-warning" : "text-muted-foreground")}>
      checked {age}
      {stale ? " — may be out of date, check again" : ""}
    </p>
  );
}

/** A live "12s ago" for a timestamp seconds old, where formatRelative says "just now". */
function useAge(iso: string): string {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);

  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "just now";

  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

function useIsStale(iso: string): boolean {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [iso]);

  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return false;
  return now - at > STALE_AFTER_MS;
}
